import { useContext, useEffect, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { Contracts } from './ui'
import { IContractReportFieldOption, IContracts, IContractsSitesForFilter, IContractsStatusesForFilter } from './types'
import { loadContractReportFieldNames, saveContractReportFieldNames } from './report-fields-storage'

import { useDict } from '@api/dict/state'
import { useLogin } from '@api/login/state'
import { useContracts } from '@api/contracts/state'
import { IContractReportFile, IContractReportJob, IContractsFilter } from '@api/contracts/state.types'

import { useValue } from '@hooks/useValue'
import { useDelayedInput } from '@hooks/useDelayedInput'

import { CardModeType } from '@smart/common.type'
import { ICardContractContainer } from '@smart/cardContract'
import { ICardListenerContainer } from '@smart/cardListener'

import { IModalCalendar } from '@primitive/modal.calendar'
import { IModalConfirm } from '@primitive/modal.confirm'
import { IModalPreview } from '@primitive/modal.preview'

import { NotificationContext, notifyOpen } from "@ui/notification/notification.provider"


const contractReportPollIntervalMs = 500
const contractReportCompletionDelayMs = 300
const wait = (delay: number) => new Promise<void>((resolve) => setTimeout(resolve, delay))

/** Договора - Контейнер */
export const ContractsContainer = () => {
    const notify = useContext(NotificationContext)

    const isAccessDeniedMessage = (message?: string) => {
        const normalizedMessage = (message || '').toLowerCase()

        return normalizedMessage.includes('доступ') || normalizedMessage.includes('допуск')
    }

    /** Метка о том, что доступ к данному разделу запрещен */
    const isAccessDenied = useValue<boolean>(false)

    const dictState = useDict(useShallow((state) => state))
    const { loginAccessSites, loginName } = useLogin(useShallow((state) => ({
        loginAccessSites: state.profile.accessSites,
        loginName: state.profile.loginName,
    })))
    const contractsState = useContracts(useShallow((state) => state))
    const appliedContractsQuery = useRef<{search: string, filter: IContractsFilter} | null>(null)

    const modalCalendar = useValue<IModalCalendar>({isOpen: false})
    const cardListener = useValue<ICardListenerContainer>({isOpen: false})
    const cardContract = useValue<ICardContractContainer>({isOpen: false})
    const contractReportsIsOpen = useValue<boolean>(false)
    const contractReportsIsLoading = useValue<boolean>(false)
    const contractReportIsCreating = useValue<boolean>(false)
    const contractReportProgress = useValue<IContractReportJob>({
        jobID: '',
        status: 'queued',
        progress: 0,
        processedRows: 0,
        totalRows: 0,
        message: 'Отчёт ожидает начала формирования...',
    })
    const contractReportFields = useValue<IContractReportFieldOption[]>([])
    const contractReportFiles = useValue<IContractReportFile[]>([])
    const contractReportConfirm = useValue<IModalConfirm>({isOpen: false})
    const contractReportPreview = useValue<IModalPreview>({isOpen: false})

    const currentPageNumber = useValue<number>(1)

    const filterIsOpen = useValue<boolean>(false)
    const filterIsActivate = useValue<boolean>(false)
    const filterSites = useValue<IContractsSitesForFilter[]>([])
    const filterContractsStatuses = useValue<IContractsStatusesForFilter[]>([])
    const filterDateStartEducationFrom = useValue<string>('')
    const filterDateStartEducationBefore = useValue<string>('')
    const filterDateEndEducationFrom = useValue<string>('')
    const filterDateEndEducationBefore = useValue<string>('')
    const filterDateCreateFrom = useValue<string>('')
    const filterDateCreateBefore = useValue<string>('')
    const filterIsCompany = useValue<boolean>(false)

    // ===================================================================================

    useEffect(() => {
        dictState.actionGetContractsStatuses().then((res) => {
            filterContractsStatuses.setValue(
                res.map((item) => ({
                    id: item.contractStatusID,
                    name: item.contractStatusName,
                    isSelected: false
                }))
            )    
        })

        contractsState.actionGetContractsStatistics()

        return (() => {
            contractsState.clearContractsState()

            currentPageNumber.setValue(1)
            setTxtSearch('')
        
            filterIsOpen.setValue(false)
            filterIsActivate.setValue(false)
            filterSites.setValue([])
            filterContractsStatuses.setValue([])
            filterDateStartEducationFrom.setValue('')
            filterDateStartEducationBefore.setValue('')
            filterDateEndEducationFrom.setValue('')
            filterDateEndEducationBefore.setValue('')
            filterDateCreateFrom.setValue('')
            filterDateCreateBefore.setValue('')
            filterIsCompany.setValue(false)
            contractReportsIsOpen.setValue(false)
            contractReportFields.setValue([])
            contractReportFiles.setValue([])
            contractReportConfirm.setValue({isOpen: false})
            contractReportPreview.setValue({isOpen: false})
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps


    useEffect(() => {
        if (loginAccessSites && loginAccessSites.length > 0) {
            filterSites.setValue(
                loginAccessSites.map((item) => ({
                    id: item.siteDbNameID,
                    name: item.siteDbName,
                    description: item.siteDbNameDescription,
                    isSelected: true
                }))
            )
        }
    }, [loginAccessSites]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!contractsState.error) return
        if (isAccessDenied.value || isAccessDeniedMessage(contractsState.error.message)) return

        return notify && notifyOpen(contractsState.error.message, contractsState.error.type, 3000, notify)
    }, [contractsState.error, isAccessDenied.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadPrimaryData(txtSearch)
    }, [currentPageNumber.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!contractsState.isReloadContracts) return

        contractsState.actionGetContractsStatistics()
        loadPrimaryData(txtSearch)
    }, [contractsState.isReloadContracts]) // eslint-disable-line react-hooks/exhaustive-deps

    // ===================================================================================

    const getContractsFilterData = (): IContractsFilter => {
        let contractStatusesListSelected: string = ''
        let accessSitesListSelected: string = ''

        if (filterContractsStatuses.value.length > 0) {
            filterContractsStatuses.value.forEach((item) => {
                if (item.isSelected) {
                    contractStatusesListSelected += `'${item.name}',`
                }
            })
        }

        if (filterSites.value.length > 0) {
            filterSites.value.forEach((item) => {
                if (item.isSelected) {
                    accessSitesListSelected += `'${item.name}',`
                }
            })
        }

        return {
            contractsStatuses: contractStatusesListSelected,
            accessSites: accessSitesListSelected,
            dateStartEducationFrom: filterDateStartEducationFrom.value,
            dateStartEducationBefore: filterDateStartEducationBefore.value,
            dateEndEducationFrom: filterDateEndEducationFrom.value,
            dateEndEducationBefore: filterDateEndEducationBefore.value,
            dateCreateFrom: filterDateCreateFrom.value,
            dateCreateBefore: filterDateCreateBefore.value,
            isCompany: filterIsCompany.value
        }
    }

    /** Функция по получению списка договоров */
    const loadPrimaryData = (search: string = txtSearch) => {
        const filterData = getContractsFilterData()
        appliedContractsQuery.current = {search, filter: filterData}

        contractsState.actionGetContracts(currentPageNumber.value, search, filterData).then((res) => {
            if (res.status !== 200 && res.status !== 500 && res.status !== 499) {
                if (res.status === 403) {
                    contractsState.clearContractsState()
                    isAccessDenied.setValue(true)
                } else {
                    notify && notifyOpen(res.message, 'error', 2000, notify)
                }
            }
        })
    }

    const handleRefreshData = () => {
        contractsState.actionGetContractsStatistics()
        loadPrimaryData(txtSearch)
    }

    /** Запуск загрузки данных через полсекунды */
    const { value: txtSearch, setValue: setTxtSearch } = useDelayedInput<string>("", 300, (val) => loadPrimaryData(val))

    const loadContractReportFiles = async () => {
        const reports = await contractsState.actionGetContractReports()
        contractReportFiles.setValue(reports)
    }

    const handleOpenContractReports = async () => {
        contractReportsIsOpen.setValue(true)
        contractReportsIsLoading.setValue(true)

        try {
            const selectedFields = new Map(loadContractReportFieldNames(loginName).map((fieldName, index) => [fieldName, index]))
            const fields = await contractsState.actionGetContractReportFields()
            contractReportFields.setValue(fields.map((field) => ({
                ...field,
                isSelected: selectedFields.has(field.fieldName),
            })).sort((first, second) => (
                (selectedFields.get(first.fieldName) ?? Number.MAX_SAFE_INTEGER)
                - (selectedFields.get(second.fieldName) ?? Number.MAX_SAFE_INTEGER)
            )))

            if (fields.length > 0) await loadContractReportFiles()
        } finally {
            contractReportsIsLoading.setValue(false)
        }
    }

    const updateContractReportFields = (fields: IContractReportFieldOption[]) => {
        const orderedFields = [...fields].sort((first, second) => Number(second.isSelected) - Number(first.isSelected))
        contractReportFields.setValue(orderedFields)
        const selectedFieldNames = orderedFields.filter((field) => field.isSelected).map((field) => field.fieldName)
        if (!saveContractReportFieldNames(loginName, selectedFieldNames)) {
            notify && notifyOpen('Не удалось запомнить выбранные поля: хранилище браузера недоступно.', 'warning', 3000, notify)
        }
    }

    const handleContractReportFieldChange = (fieldName: string) => {
        updateContractReportFields(contractReportFields.value.map((field) => (
            field.fieldName === fieldName ? {...field, isSelected: !field.isSelected} : field
        )))
    }

    const handleContractReportFieldMove = (fieldName: string, targetFieldName: string) => {
        const fields = [...contractReportFields.value]
        const sourceIndex = fields.findIndex((field) => field.isSelected && field.fieldName === fieldName)
        const targetIndex = fields.findIndex((field) => field.isSelected && field.fieldName === targetFieldName)
        if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return

        const [movedField] = fields.splice(sourceIndex, 1)
        fields.splice(targetIndex, 0, movedField)
        updateContractReportFields(fields)
    }

    const handleContractReportFieldsAllChange = () => {
        const isAllSelected = contractReportFields.value.length > 0
            && contractReportFields.value.every((field) => field.isSelected)
        updateContractReportFields(contractReportFields.value.map((field) => ({
            ...field,
            isSelected: !isAllSelected,
        })))
    }

    const handleCreateContractReport = async () => {
        if (contractReportIsCreating.value) return

        const selectedFieldNames = contractReportFields.value
            .filter((field) => field.isSelected)
            .map((field) => field.fieldName)
        if (selectedFieldNames.length === 0) {
            notify && notifyOpen('Выберите хотя бы одно поле для формирования отчёта.', 'warning', 2500, notify)
            return
        }

        const query = appliedContractsQuery.current
        if (!query) return

        contractReportProgress.setValue({
            jobID: '',
            status: 'queued',
            progress: 0,
            processedRows: 0,
            totalRows: 0,
            message: 'Отчёт ожидает начала формирования...',
        })
        contractReportIsCreating.setValue(true)
        try {
            const response = await contractsState.actionSetContractReportCreate(
                query.search,
                query.filter,
                selectedFieldNames,
            )
            const jobID = response.data?.jobID
            if (response.status !== 200 || !jobID) return

            while (true) {
                const progressResponse = await contractsState.actionGetContractReportProgress(jobID)
                const job = progressResponse.data?.job
                if (progressResponse.status !== 200 || !job) return

                contractReportProgress.setValue(job)
                if (job.status === 'failed') return
                if (job.status === 'completed') break

                await wait(contractReportPollIntervalMs)
            }

            await Promise.all([
                loadContractReportFiles(),
                wait(contractReportCompletionDelayMs),
            ])
            notify && notifyOpen('Отчёт по договорам сформирован.', 'success', 2500, notify)
        } finally {
            contractReportIsCreating.setValue(false)
        }
    }

    const handlePreviewContractReport = (reportID: number) => {
        const report = contractReportFiles.value.find((item) => item.contractReportID === reportID)
        if (!report) return

        contractReportPreview.setValue({
            isOpen: true,
            fileUrl: report.filePath,
            fileName: report.fileName,
            handleButtonCancel() {contractReportPreview.setValue({isOpen: false})},
        })
    }

    const handleDownloadContractReport = (reportID: number) => {
        const report = contractReportFiles.value.find((item) => item.contractReportID === reportID)
        if (!report) return

        const link = document.createElement('a')
        const downloadUrl = new URL(report.filePath, window.location.origin)
        downloadUrl.searchParams.set('download', '1')
        link.href = downloadUrl.toString()
        link.download = report.fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    const handleDeleteContractReport = (reportID: number) => {
        const report = contractReportFiles.value.find((item) => item.contractReportID === reportID)
        if (!report) return

        contractReportConfirm.setValue({
            isOpen: true,
            headerText: 'Удаление отчёта',
            contentText: `Удалить файл отчёта «${report.fileName}»?`,
            handleButtonCancel() {contractReportConfirm.setValue({isOpen: false})},
            async handleButtonOk() {
                contractReportConfirm.setValue((currentValue) => ({...currentValue, isLoading: true}))
                const response = await contractsState.actionSetContractReportDelete(reportID)
                if (response.status === 200) {
                    await loadContractReportFiles()
                    notify && notifyOpen('Отчёт удалён из списка.', 'success', 2000, notify)
                }
                contractReportConfirm.setValue({isOpen: false})
            },
        })
    }

    // ===================================================================================

    /** Кнопка применить (очистить) в окне - Фильтр по договорам */
    const handleApplyClearFilter = (filterType: 'apply' | 'clear') => {
        switch (filterType) {
            case 'apply': {
                // Фильтр активен только если значения отличаются от начальных
                const isFilterActive = 
                    filterSites.value.some(s => !s.isSelected) ||
                    filterContractsStatuses.value.some(s => s.isSelected) ||
                    filterDateStartEducationFrom.value !== '' ||
                    filterDateStartEducationBefore.value !== '' ||
                    filterDateEndEducationFrom.value !== '' ||
                    filterDateEndEducationBefore.value !== '' ||
                    filterDateCreateFrom.value !== '' ||
                    filterDateCreateBefore.value !== '' ||
                    filterIsCompany.value !== false
                filterIsActivate.setValue(isFilterActive)
                loadPrimaryData(txtSearch)
                break
            }

            case 'clear': {
                filterIsActivate.setValue(false)
                filterSites.setValue(filterSites.value.map(site => ({...site, isSelected: true})))
                filterContractsStatuses.setValue(filterContractsStatuses.value.map(status => ({...status, isSelected: false})))
                filterDateStartEducationFrom.setValue('')
                filterDateStartEducationBefore.setValue('')
                filterDateEndEducationFrom.setValue('')
                filterDateEndEducationBefore.setValue('')
                filterDateCreateFrom.setValue('')
                filterDateCreateBefore.setValue('')
                filterIsCompany.setValue(false)

                const filterData: IContractsFilter = {
                    contractsStatuses: '',
                    accessSites: '',
                    dateStartEducationFrom: '', dateStartEducationBefore: '',
                    dateEndEducationFrom: '', dateEndEducationBefore: '',
                    dateCreateFrom: '', dateCreateBefore: '', isCompany: false
                }
        
                appliedContractsQuery.current = {search: txtSearch, filter: filterData}
                contractsState.actionGetContracts(currentPageNumber.value, txtSearch, filterData).then(() => {
                    filterIsActivate.setValue(false)
                })
                break
            }
        }
    }

    /** Открытие/закрытие окна выбора даты в календаре */
    const handleOpenModalCalendar = (isOpen: boolean, fieldName: string) => {
        modalCalendar.setValue({
            isOpen,
            handleButtonCancel() { modalCalendar.setValue({ isOpen: false }) },
            handleButtonOk(stringDate) {
                switch (fieldName) {
                    case 'dateStartEducationFrom': filterDateStartEducationFrom.setValue(stringDate); break
                    case 'dateStartEducationBefore': filterDateStartEducationBefore.setValue(stringDate); break
                    case 'dateEndEducationFrom': filterDateEndEducationFrom.setValue(stringDate); break
                    case 'dateEndEducationBefore': filterDateEndEducationBefore.setValue(stringDate); break
                    
                    case 'dateCreateFrom': filterDateCreateFrom.setValue(stringDate); break
                    case 'dateCreateBefore': filterDateCreateBefore.setValue(stringDate); break
                }

                modalCalendar.setValue({ isOpen: false })
            }
        })
    }

    /** Открытие/закрытие анкеты по договору */
    const handleOpenCardContract = (isOpen: boolean, cardMode: CardModeType, cardID?: number) => {
        cardContract.setValue({
            isOpen, cardMode, cardID,
            handlerButtonCancel() {cardContract.setValue({isOpen: false})}, 
            handlerButtonOk() {
                cardContract.setValue({isOpen: false})
                loadPrimaryData(txtSearch)
            }
        })
    }

    /** Открытие/закрытие анкеты по слушателю */
    const handleOpenCardListener = (isOpen: boolean, cardMode: CardModeType, cardID?: number) => {
        cardListener.setValue({
            isOpen, cardMode, cardID,
            handlerButtonCancel() {cardListener.setValue({isOpen: false})}, 
            handlerButtonOk() {
                cardListener.setValue({isOpen: false})
                loadPrimaryData(txtSearch)
            }
        })
    }
    
    // ===================================================================================

    /** Свойства передаваемые в компоненту */
    const propsToComponent: IContracts = {
        isAccessDenied,
        txtSearch, setTxtSearch, handleRefreshData, loadPrimaryData, 

        filterIsActivate, filterIsOpen, filterSites, filterContractsStatuses,
        filterDateStartEducationFrom, filterDateStartEducationBefore,
        filterDateEndEducationFrom, filterDateEndEducationBefore,
        filterDateCreateFrom, filterDateCreateBefore, filterIsCompany, handleApplyClearFilter,

        modalCalendar, handleOpenModalCalendar,
        cardContract, handleOpenCardContract,
        cardListener, handleOpenCardListener,

        contractsStatistics: contractsState.contractsStatistics,
        contractsList: contractsState.contracts.items,

        currentPageNumber, 
        isLoading: contractsState.contracts.isLoading,
        totalCount: contractsState.contracts.totalCount, 
        pageSize: contractsState.contracts.pageSize,

        contractReports: {
            isOpen: contractReportsIsOpen.value,
            isLoading: contractReportsIsLoading.value,
            isCreating: contractReportIsCreating.value,
            creationProgress: contractReportProgress.value,
            fields: contractReportFields.value,
            reports: contractReportFiles.value,
            modalConfirm: contractReportConfirm.value,
            modalPreview: contractReportPreview.value,
            handleButtonOpen: handleOpenContractReports,
            handleButtonCancel: () => contractReportsIsOpen.setValue(false),
            handleFieldChange: handleContractReportFieldChange,
            handleFieldMove: handleContractReportFieldMove,
            handleFieldsAllChange: handleContractReportFieldsAllChange,
            handleCreate: handleCreateContractReport,
            handlePreview: handlePreviewContractReport,
            handleDownload: handleDownloadContractReport,
            handleDelete: handleDeleteContractReport,
        },
    }

    return <>
        <Contracts {...propsToComponent} />
    </>
}